// src/components/RandomPalette/RandomPalette.js
// Ultra-compact version using organized palette data

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './RandomPalette.css';
import Header from '../Header/Header';
import PaletteDisplay from '../PaletteDisplay/PaletteDisplay';
import MatchResults from '../MatchResults/MatchResults';
import { getRandomPaletteForTheme, getThemesList } from '../../data/colorPalettes';

const API_BASE = (process.env.REACT_APP_MATCH_API || "https://k6aslecb84.execute-api.us-west-2.amazonaws.com/prod").replace(/\/$/, "");

const RandomPalette = () => {
  const [palette, setPalette] = useState([]);
  const [currentTheme, setCurrentTheme] = useState('fantasy');
  const [currentPaletteName, setCurrentPaletteName] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [error, setError] = useState('');

  // Get themes from data file
  const themes = getThemesList();

  // Generate new palette from selected theme
  const generateNewPalette = () => {
    setError('');
    setMatches([]);
    
    const paletteData = getRandomPaletteForTheme(currentTheme);
    
    if (paletteData) {
      setPalette(paletteData.colors);
      setCurrentPaletteName(paletteData.name);
    } else {
      setError('Failed to load palette. Please try again.');
    }
  };

  // Fetch paint matches for the palette
  const fetchMatches = async () => {
    if (!palette || palette.length === 0) return;
    
    setMatchesLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/match-hex`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hexes: palette, topN: 3 })
      });
      
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API ${response.status}: ${text}`);
      }
      
      const data = await response.json();
      setMatches(data.results || []);
    } catch (err) {
      console.error('Error matching paints:', err);
      setError('Failed to match paints. Please try again.');
    } finally {
      setMatchesLoading(false);
    }
  };

  // Generate initial palette on mount
  useEffect(() => {
    generateNewPalette();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch matches when palette changes
  useEffect(() => {
    if (palette && palette.length > 0) {
      fetchMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette]);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Random Palette Generator for Miniature Painting | Free Color Schemes</title>
        <meta name="title" content="Random Palette Generator for Miniature Painting | Free Color Schemes" />
        <meta 
          name="description" 
          content="Generate inspiring color palettes for miniature painting, Warhammer, tabletop gaming, and hobby models. 100+ professional paint schemes across 15 themes. Match colors to real paints instantly." 
        />
        <meta 
          name="keywords" 
          content="miniature painting, color palette generator, paint schemes, warhammer painting, tabletop gaming colors, hobby painting, model painting colors, color inspiration, paint combinations, grimdark palette, fantasy colors, sci-fi painting, color theory, palette ideas, paint matcher, citadel colors, vallejo colors, army painter" 
        />
        <link rel="canonical" href="https://yourdomain.com/random-palette" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/random-palette" />
        <meta property="og:title" content="Random Palette Generator for Miniature Painting | Free Color Schemes" />
        <meta 
          property="og:description" 
          content="Generate inspiring color palettes for miniature painting, Warhammer, tabletop gaming, and hobby models. 100+ professional paint schemes." 
        />
        <meta property="og:image" content="https://yourdomain.com/og-image-palette.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourdomain.com/random-palette" />
        <meta property="twitter:title" content="Random Palette Generator for Miniature Painting" />
        <meta 
          property="twitter:description" 
          content="Generate inspiring color palettes for miniature painting, Warhammer, tabletop gaming, and hobby models." 
        />
        <meta property="twitter:image" content="https://yourdomain.com/og-image-palette.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Miniature Painting Palette Generator",
            "description": "Free color palette generator for miniature painting, Warhammer, and tabletop gaming. Generate professional paint schemes and match to real paints.",
            "url": "https://yourdomain.com/random-palette",
            "applicationCategory": "DesignApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "100+ curated color palettes",
              "15 themed categories",
              "Instant paint matching",
              "Professional color schemes",
              "Fantasy, sci-fi, and grimdark themes"
            ],
            "keywords": "miniature painting, color palette, warhammer, tabletop gaming, hobby painting, paint schemes"
          })}
        </script>
      </Helmet>
      <Header />
      <div className="random-palette-page">
        <header className="page-header">
          <div className="header-content">
            <div className="header-icon" role="img" aria-label="Paint palette icon">🎨</div>
            <h1>Miniature Painting Palette Generator</h1>
            <p className="subtitle">
              Generate professional color schemes for Warhammer, tabletop gaming, and hobby miniatures. Instantly match colors to real paints.
            </p>
            <div className="header-accent-line" aria-hidden="true"></div>
          </div>
        </header>

        <main className="random-palette-content">
          {/* TWO COLUMN LAYOUT: Theme selector + Generate button */}
          <div className="controls-wrapper">
            <div className="theme-selector-section">
              <h2>Choose Theme</h2>
              <div className="theme-grid">
                {themes.map(theme => (
                  <button
                    key={theme.id}
                    className={`theme-card ${currentTheme === theme.id ? 'active' : ''}`}
                    onClick={() => setCurrentTheme(theme.id)}
                    title={`${theme.paletteCount} palettes available`}
                  >
                    <span className="theme-icon">{theme.icon}</span>
                    <span className="theme-name">{theme.name}</span>
                    <span className="theme-description">{theme.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="generator-section">
              <h3>Generate</h3>
              <button onClick={generateNewPalette} className="generate-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                </svg>
                <span>New Palette</span>
              </button>
            </div>
          </div>

          {palette && palette.length > 0 && (
            <div className="palette-section">
              <h2>
                {currentPaletteName}{' '}
                <span className="palette-subtitle">
                  {themes.find(t => t.id === currentTheme)?.name} Theme
                </span>
              </h2>
              <PaletteDisplay palette={palette} />
            </div>
          )}

          {error && (
            <div className="error-message" role="alert">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {matchesLoading && (
            <div className="matches-loading">
              <div className="spinner" />
              <p>Finding paint matches...</p>
            </div>
          )}

          {matches && matches.length > 0 && !matchesLoading && (
            <div className="matches-section">
              <h2>Matching Paints</h2>
              <MatchResults matches={matches} palette={palette} />
            </div>
          )}

          <section className="info-section" aria-labelledby="how-it-works">
            <h2 id="how-it-works">How This Miniature Paint Palette Generator Works</h2>
            <ol className="info-list">
              <li><strong>Choose Your Theme:</strong> Select from {themes.length} themed categories including grimdark, fantasy, sci-fi, cyberpunk, and more. Over {themes.reduce((sum, t) => sum + t.paletteCount, 0)}+ hand-crafted color palettes designed by professional miniature painters.</li>
              <li><strong>Generate Color Schemes:</strong> Each palette contains 6 carefully selected colors with proper shadow-to-highlight progression. Perfect for Warhammer 40k, Age of Sigmar, D&D miniatures, and any tabletop gaming models.</li>
              <li><strong>Instant Paint Matching:</strong> Colors are automatically matched to real hobby paints using the ΔE2000 color difference formula. Find equivalent paints from Citadel, Vallejo, Army Painter, and other major brands.</li>
              <li><strong>Unlimited Inspiration:</strong> Generate endless unique palettes for your painting projects. Each theme includes multiple variations to inspire your next army, warband, or display piece.</li>
            </ol>
          </section>

          <section className="seo-content" aria-labelledby="about-tool">
            <h2 id="about-tool">Professional Color Palettes for Miniature Painting</h2>
            <p>
              Our free palette generator is designed specifically for hobbyists painting Warhammer miniatures, tabletop gaming models, and display pieces. Whether you're painting Space Marines, fantasy armies, cyberpunk mechs, or grimdark chaos warriors, our curated color schemes provide professional-quality inspiration.
            </p>
            <p>
              Each palette is crafted with color theory principles used by award-winning miniature painters. The six-color format includes shadow tones, base colors, highlights, and accent colors - everything needed for a complete paint job. Themes range from classic fantasy and sci-fi to specialized categories like steampunk, undead, alien worlds, and military camouflage.
            </p>
            <h3>Perfect for All Skill Levels</h3>
            <p>
              Whether you're a beginner learning color theory or an experienced painter seeking fresh inspiration, these palettes work for any miniature painting project. Use them for Warhammer 40,000, Age of Sigmar, Dungeons & Dragons, Star Wars Legion, or any hobby miniatures. Each color is matched to real paints, making it easy to recreate these schemes with your existing paint collection.
            </p>
          </section>
        </main>
      </div>
    </>
  );
};

export default RandomPalette;